import { getAuthentication } from "../../lib/auth";
import { MAIN_WEBSITES_URL } from "../../lib/env";
import { escapeGqlString, gqlRequest } from "../../lib/functions";
import { MAINWEBSITEGQLSCHEMA } from "../../lib/gql/MainWebsite";
import { ARTICLERICHTEXTGQLSCHEMA } from "../../lib/gql/Top10Article";
import { IMainWebsite } from "../../types/MainWebsite";
import { IMultiLanguageInputArgs } from "../../types/MultiLanguageInput";
import { ITop10ArticleRichtText } from "../../types/Top10Article.RichText";

export async function getMainWebsites(
  limit: number,
  offset: number,
  query: string
): Promise<IMainWebsite[]> {
  let filters = "";
  if (query && query.trim() !== "") {
    filters = `,query:"${query}"`;
  }
  const gqlString = `query{
        GetMainWebsites(limit:${limit},offset:${offset}${filters}){
            ${MAINWEBSITEGQLSCHEMA}
        }
    }`;
  try {
    const response = await gqlRequest<{
      GetMainWebsites: IMainWebsite[];
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.GetMainWebsites || [];
  } catch (err) {
    throw err;
  }
}

export async function getMainWebsiteByID(
  id: string
): Promise<IMainWebsite | null> {
  const gqlString = `query{
        GetMainWebsiteByID(id:"${id}"){
            ${MAINWEBSITEGQLSCHEMA}
        }
    }`;
  try {
    const response = await gqlRequest<{
      GetMainWebsiteByID: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.GetMainWebsiteByID || null;
  } catch (err) {
    throw err;
  }
}

export async function getRichTextByID(
  id: string
): Promise<ITop10ArticleRichtText | null> {
  const gqlString = `query{
          GetRichTextByID(id:"${id}"){
              ${ARTICLERICHTEXTGQLSCHEMA}
          }
      }`;
  try {
    const response = await gqlRequest<{
      GetRichTextByID: ITop10ArticleRichtText;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.GetRichTextByID || null;
  } catch (err) {
    throw err;
  }
}

export async function addNewMainWebsite(
  title: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
           AddNewMainWebsite(title:{
               en:"${escapeGqlString(title.en)}",
               ar:"${escapeGqlString(title.ar)}",
               fr:"${escapeGqlString(title.fr)}"
           }){
               ${MAINWEBSITEGQLSCHEMA}    
           }
       }`;

  try {
    const response = await gqlRequest<{
      AddNewMainWebsite: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.AddNewMainWebsite || null;
  } catch (err) {
    throw err;
  }
}

export async function editMainWebsiteTitle(
  id: string,
  title: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
        EditMainWebsiteTitle(id:"${id}",title:{
               en:"${escapeGqlString(title.en)}",
               ar:"${escapeGqlString(title.ar)}",
               fr:"${escapeGqlString(title.fr)}"
        }){
            ${MAINWEBSITEGQLSCHEMA}       
        }
    }`;
  try {
    const response = await gqlRequest<{
      EditMainWebsiteTitle: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.EditMainWebsiteTitle || null;
  } catch (err) {
    throw err;
  }
}

export async function editMainWebsiteDescription(
  id: string,
  description: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
        EditMainWebsiteDescription(id:"${id}",description:{
               en:"${escapeGqlString(description.en)}",
               ar:"${escapeGqlString(description.ar)}",
               fr:"${escapeGqlString(description.fr)}"
        }){
            ${MAINWEBSITEGQLSCHEMA}       
        }
    }`;
  try {
    const response = await gqlRequest<{
      EditMainWebsiteDescription: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.EditMainWebsiteDescription || null;
  } catch (err) {
    throw err;
  }
}

export async function pushKeywordIntoMainWebsite(
  id: string,
  keyword: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
        PushKeywordIntoMainWebsite(id:"${id}",keyword:{
               en:"${escapeGqlString(keyword.en)}",
               ar:"${escapeGqlString(keyword.ar)}",
               fr:"${escapeGqlString(keyword.fr)}"
        }){
            ${MAINWEBSITEGQLSCHEMA}       
        }
    }`;
  try {
    const response = await gqlRequest<{
      PushKeywordIntoMainWebsite: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.PushKeywordIntoMainWebsite || null;
  } catch (err) {
    throw err;
  }
}

export async function pullKeywordFromMainWebsite(
  id: string,
  keyword: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
        PullKeywordFromMainWebsite(id:"${id}",keyword:{
               en:"${escapeGqlString(keyword.en)}",
               ar:"${escapeGqlString(keyword.ar)}",
               fr:"${escapeGqlString(keyword.fr)}"
        }){
            ${MAINWEBSITEGQLSCHEMA}       
        }
    }`;
  try {
    const response = await gqlRequest<{
      PullKeywordFromMainWebsite: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.PullKeywordFromMainWebsite || null;
  } catch (err) {
    throw err;
  }
}

export async function updateMainWebsiteImage(
  id: string,
  image: string
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    UpdateMainWebsiteImage(id:"${id}",image:"${image}"){
      ${MAINWEBSITEGQLSCHEMA}
    }
  }`;
  try {
    const response = await gqlRequest<{
      UpdateMainWebsiteImage: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.UpdateMainWebsiteImage || null;
  } catch (err) {
    throw err;
  }
}

export async function setMainWebsiteHomepage(
  id: string,
  homepage: string
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    SetMainWebsiteHomepage(id:"${id}",homepage:"${homepage}"){
      ${MAINWEBSITEGQLSCHEMA}
    }
  }`;
  try {
    const response = await gqlRequest<{
      SetMainWebsiteHomepage: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.SetMainWebsiteHomepage || null;
  } catch (err) {
    throw err;
  }
}

export async function addNewMainWebsiteCategory(
  id: string,
  title: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    AddNewMainWebsiteCategory(id:"${id}",title:{
               en:"${escapeGqlString(title.en)}",
               ar:"${escapeGqlString(title.ar)}",
               fr:"${escapeGqlString(title.fr)}"
        }){
          ${MAINWEBSITEGQLSCHEMA}       
        }
  }`;
  try {
    const response = await gqlRequest<{
      AddNewMainWebsiteCategory: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.AddNewMainWebsiteCategory || null;
  } catch (err) {
    throw err;
  }
}

export async function pullMainWebsiteCategory(
  id: string,
  categoryId: string
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    PullMainWebsiteCategory(id:"${id}",categoryId:"${categoryId}"){
          ${MAINWEBSITEGQLSCHEMA}       
    }
  }`;

  try {
    const response = await gqlRequest<{
      PullMainWebsiteCategory: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.PullMainWebsiteCategory || null;
  } catch (err) {
    throw err;
  }
}

export async function editMainWebsiteCategoryTitle(
  id: string,
  categoryId: string,
  title: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    EditMainWebsiteCategoryTitle(id:"${id}",categoryId:"${categoryId}",title:{
               en:"${escapeGqlString(title.en)}",
               ar:"${escapeGqlString(title.ar)}",
               fr:"${escapeGqlString(title.fr)}"
        }){
          ${MAINWEBSITEGQLSCHEMA}       
        }
  }`;

  try {
    const response = await gqlRequest<{
      EditMainWebsiteCategoryTitle: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.EditMainWebsiteCategoryTitle || null;
  } catch (err) {
    throw err;
  }
}

export async function editMainWebsiteCategoryDescription(
  id: string,
  categoryId: string,
  description: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    EditMainWebsiteCategoryDescription(id:"${id}",categoryId:"${categoryId}",description:{
               en:"${escapeGqlString(description.en)}",
               ar:"${escapeGqlString(description.ar)}",
               fr:"${escapeGqlString(description.fr)}"
        }){
          ${MAINWEBSITEGQLSCHEMA}       
        }
  }`;
  try {
    const response = await gqlRequest<{
      EditMainWebsiteCategoryDescription: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.EditMainWebsiteCategoryDescription || null;
  } catch (err) {
    throw err;
  }
}

export async function addKeywordToMainWebsiteCategory(
  id: string,
  categoryId: string,
  keyword: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    AddKeywordToMainWebsiteCategory(id:"${id}",categoryId:"${categoryId}",keyword:{
               en:"${escapeGqlString(keyword.en)}",
               ar:"${escapeGqlString(keyword.ar)}",
               fr:"${escapeGqlString(keyword.fr)}"
        }){
          ${MAINWEBSITEGQLSCHEMA}       
        }
  }`;
  try {
    const response = await gqlRequest<{
      AddKeywordToMainWebsiteCategory: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.AddKeywordToMainWebsiteCategory || null;
  } catch (err) {
    throw err;
  }
}

export async function removeKeywordFromMainWebsiteCategory(
  id: string,
  categoryId: string,
  keyword: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    RemoveKeywordFromMainWebsiteCategory(id:"${id}",categoryId:"${categoryId}",keyword:{
               en:"${escapeGqlString(keyword.en)}",
               ar:"${escapeGqlString(keyword.ar)}",
               fr:"${escapeGqlString(keyword.fr)}"
        }){
          ${MAINWEBSITEGQLSCHEMA}       
        }
  }`;
  try {
    const response = await gqlRequest<{
      RemoveKeywordFromMainWebsiteCategory: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.RemoveKeywordFromMainWebsiteCategory || null;
  } catch (err) {
    throw err;
  }
}

export async function editMainWebsiteCategoryImage(
  id: string,
  categoryId: string,
  image: string
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    EditMainWebsiteCategoryImage(id:"${id}",categoryId:"${categoryId}",image:"${image}"){
      ${MAINWEBSITEGQLSCHEMA}
    }
  }`;
  try {
    const response = await gqlRequest<{
      EditMainWebsiteCategoryImage: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.EditMainWebsiteCategoryImage || null;
  } catch (err) {
    throw err;
  }
}

export async function addTop10ArticleToCategory(
  websiteId: string,
  categoryId: string,
  articleId: string
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    AddTop10ArticleToCategory(websiteId:"${websiteId}",categoryId:"${categoryId}",articleId:"${articleId}"){
      ${MAINWEBSITEGQLSCHEMA}
    }
  }`;
  try {
    const response = await gqlRequest<{
      AddTop10ArticleToCategory: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.AddTop10ArticleToCategory || null;
  } catch (err) {
    throw err;
  }
}

export async function removeTop10ArticleFromCategory(
  websiteId: string,
  categoryId: string,
  articleId: string
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    RemoveTop10ArticleFromCategory(websiteId:"${websiteId}",categoryId:"${categoryId}",articleId:"${articleId}"){
      ${MAINWEBSITEGQLSCHEMA}
    }
  }`;
  try {
    const response = await gqlRequest<{
      RemoveTop10ArticleFromCategory: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.RemoveTop10ArticleFromCategory || null;
  } catch (err) {
    throw err;
  }
}

export async function addRichTextToCategory(
  id: string,
  categoryId: string,
  title: IMultiLanguageInputArgs,
  content: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    AddRichTextToCategory(id:"${id}",categoryId:"${categoryId}",title:{
               en:"${escapeGqlString(title.en)}",
               ar:"${escapeGqlString(title.ar)}",
               fr:"${escapeGqlString(title.fr)}"
        },content:{
               en:"${escapeGqlString(content.en)}",
               ar:"${escapeGqlString(content.ar)}",
               fr:"${escapeGqlString(content.fr)}"
        }){
      ${MAINWEBSITEGQLSCHEMA}
    }
  }`;
  try {
    const response = await gqlRequest<{
      AddRichTextToCategory: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.AddRichTextToCategory || null;
  } catch (err) {
    throw err;
  }
}

export async function removeRichTextFromCategory(
  websiteId: string,
  categoryId: string,
  richTextId: string
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    RemoveRichTextFromCategory(websiteId:"${websiteId}",categoryId:"${categoryId}",richTextId:"${richTextId}"){
      ${MAINWEBSITEGQLSCHEMA}
    }
  }`;
  try {
    const response = await gqlRequest<{
      RemoveRichTextFromCategory: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response.data?.RemoveRichTextFromCategory || null;
  } catch (err) {
    throw err;
  }
}

export async function sortCategortSortedItems(
  id: string,
  categoryId: string,
  newOrder: { Type: "LIST" | "TEXT"; RichText?: string }[]
): Promise<IMainWebsite | undefined | null> {
  const newOrderString = newOrder
    .map((item) => {
      const richTextPart = item.RichText
        ? `, RichText: "${item.RichText}"`
        : "";
      return `{ Type: ${item.Type}${richTextPart} }`;
    })
    .join(", ");

  const gqlString = `mutation {
      SortCategortSortedItems(id: "${id}",categoryId:"${categoryId}", newOrder: [${newOrderString}]) {
        ${MAINWEBSITEGQLSCHEMA}
      }
    }`;

  try {
    const response = await gqlRequest<{
      SortCategortSortedItems: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response?.data?.SortCategortSortedItems || null;
  } catch (err) {
    throw err;
  }
}

export async function editRichTextByID(
  id: string,
  title: IMultiLanguageInputArgs,
  content: IMultiLanguageInputArgs
): Promise<IMainWebsite | null> {
  const gqlString = `mutation{
    EditRichTextByID(id:"${id}",title:{
               en:"${escapeGqlString(title.en)}",
               ar:"${escapeGqlString(title.ar)}",
               fr:"${escapeGqlString(title.fr)}"
        },content:{
               en:"${escapeGqlString(content.en)}",
               ar:"${escapeGqlString(content.ar)}",
               fr:"${escapeGqlString(content.fr)}"
        }){
          ${ARTICLERICHTEXTGQLSCHEMA}       
        }
  }`;
  try {
    const response = await gqlRequest<{
      EditRichTextByID: IMainWebsite;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response?.data?.EditRichTextByID || null;
  } catch (err) {
    throw err;
  }
}

export async function deleteMainWebsite(
  id: string
): Promise<boolean | undefined> {
  const gqlString = `mutation{
    DeleteMainWebsite(id:"${id}")
  }`;
  try {
    const response = await gqlRequest<{
      DeleteMainWebsite: boolean;
    }>({
      query: gqlString,
      endpoint: MAIN_WEBSITES_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }

    return response?.data?.DeleteMainWebsite;
  } catch (err) {
    throw err;
  }
}
